import {inject, Injectable, PLATFORM_ID, signal} from "@angular/core";
import {CoreModel} from "../_models/core.model";
import {UserSession} from "../../common/interfaces/domain/UserSession";
import {AbstractController} from "../../common/controllers/AbstractController";
import {AppEventBus} from "../../common/event-buses/app.event-bus";
import {AppEvent} from "../../common/event-buses/AppEvent";
import {AppEventName} from "../../common/event-buses/AppEventName";
import {TranslateService} from "@ngx-translate/core";
import {ModelKey} from "../../common/models/ModelKey";
import {ClonedLineMappingRepository} from "../../common/repositories/cloned-line-mapping.repository";
import {AppStatusEvent} from "../../common/events/AppStatusEvent";
import {SocketIoService} from "../../common/services/socket-io.service";
import {ProgressTrackableItem} from "../../common/interfaces/domain/ProgressTrackableItem";
import {AlertInstance} from "../../common/interfaces/domain/alert/AlertInstance";
import {ToastAlertService} from "../../common/services/toast-alert.service";
import {AuthenticationService} from "../../common/services/authentication.service";
import {ToastMessageService} from "../../common/services/toast-message.service";
import {UserSessionRepository} from "../../common/repositories/user-session.repository";
import {AlertNotificationWebsocketService} from "../../common/services/alert-notification-websocket.service";
import {AccessControlService} from "../../common/services/access-control.service";
import {GlobalConfig} from "../../common/configs/GlobalConfig";
import {CommonConstants} from "../../common/constants/CommonConstants";
import {interval} from "rxjs";
import {BuildInfoRepository} from "../../common/repositories/build-info.repository";
import {isPlatformBrowser} from "@angular/common";
import {PendoService} from "../../common/services/pendo.service";
import {UserRepository} from "../../common/repositories/user.repository";
import {LocalStorageRepository} from "../../common/repositories/local-storage.repository";

export const MK_CORE = new ModelKey<CoreModel>('MK_CORE');

@Injectable()
export class CoreController extends AbstractController {

  private readonly userSessionRepository = inject(UserSessionRepository);

  // This injection is required since this service is more like a background service.
  private readonly alertNotificationWebsocketService = inject(AlertNotificationWebsocketService);

  private platformId = inject(PLATFORM_ID);

  private readonly buildInfoCheckObservable = interval(60000);
  private outdatedBuild = false;
  private snoozeRequest = false;
  private hideAutoUpdateToastAfterSnoozed = false;
  private previousCountDownTimestamp = -1;
  private readonly autoUpdateCountDown: number = 59;
  private readonly autoUpdateCountDownAfterSnoozed: number = 600;
  private readonly autoUpdateMessage: string = 'A newer version of this app is available';

  constructor(private appEventBus: AppEventBus,
              private translateService: TranslateService,
              private socketIoService: SocketIoService,
              private toastMessageService: ToastMessageService,
              private toastAlertService: ToastAlertService,
              private authenticationService: AuthenticationService,
              private accessControlService: AccessControlService,
              private pendoService: PendoService,
              private localStorageRepository: LocalStorageRepository,
              private userRepository: UserRepository,
              private clonedLineMappingRepository: ClonedLineMappingRepository,
              private buildInfoRepository: BuildInfoRepository) {
    super();

    this.addModel(MK_CORE, {
      appVersion$: signal(GlobalConfig.version),
      production$: signal(GlobalConfig.production),
      buildNumber$: signal(GlobalConfig.buildNumber),
      buildTimestamp$: signal(GlobalConfig.buildTimestamp),
      userSession$: signal(new UserSession()),
      userLoggedIn$: signal(false),
      impersonation$: signal(false),
      translationEnabled$: signal(true),
      alertNotificationEnabled$: signal(true),
      translationLoaded$: signal(false),
      showProgressBar$: signal(false),
      progressTrackableItems$: signal([]),
      progressValue$: signal(0),
      appNavigationFlags$: signal({
        enableNavigation$: signal(true),
        showNavigation$: signal(true),
        mobileNavigation$: signal(false),
        disablePinning$: signal(false),
        pinned$: signal(this.localStorageRepository.getNavigationPinPreference()),
        hovered$: signal(false),
      }),
      autoUpdateCountDownMessage$: signal(''),
    });

    this.sub(this.appEventBus.subscribe(AppEventName.SessionExpiration, (event) => this.onSessionExpiration(event)));
    this.sub(this.appEventBus.subscribe(AppEventName.NewUserSession, (event) => this.onNewUserSession(event)));
    this.sub(this.appEventBus.subscribe(AppEventName.NavigationData, (event) => this.onNavigationData(event)));
    this.sub(this.appEventBus.subscribe(AppEventName.LoggedIn, (event) => this.onLoggedIn(event)));
    this.sub(this.appEventBus.subscribe(AppEventName.AddAppStatus, (event) => this.onAddAppStatus(event)));
    this.sub(this.appEventBus.subscribe(AppEventName.RemoveAppStatus, (event) => this.onRemoveAppStatus(event)));
    this.sub(this.appEventBus.subscribe(AppEventName.AddProgressTrackableItem, (event) => this.onAddProgressTrackableItem(event)));
    this.sub(this.appEventBus.subscribe(AppEventName.RemoveProgressTrackableItem, (event) => this.onRemoveProgressTrackableItem(event)));
    this.sub(this.appEventBus.subscribe(AppEventName.AlertNotification, (event) => this.onAlertNotification(event)));
    this.sub(this.appEventBus.subscribe(AppEventName.LogoutRequest, (event) => this.onLogoutRequest(event)));
    this.sub(this.appEventBus.subscribe(AppEventName.LeaveImpersonation, (event) => this.onLeaveImpersonation(event)));

    this.sub(this.translateService.onLangChange.subscribe((event) => {
      if(event) {
        this.onTranslationLoaded(event);
      }
    }));

    if(GlobalConfig.buildNumber !== '{{buildNumber}}') {
      // When running locally, buildNumber is of the value {{buildNumber}}.
      // We only want to do the check but not running locally.
      this.sub(this.buildInfoCheckObservable.subscribe((event) => {
        this.onBuildInfoCheck();
      }));
    }

    if(isPlatformBrowser(this.platformId)) {
      this.setupGlobalEventListeners();
    }
  }

  getCoreModel(): CoreModel {
    return this.getModel(MK_CORE);
  }

  private handleInit(eventData: any): void {
    this.screenSizeAware();
  }

  private handleToggleTheme() {
    const currentTheme = document.body.getAttribute('data-theme');

    if(currentTheme === 'dark') {
      document.body.setAttribute('data-theme', 'light');
    }
    else if(currentTheme === 'light') {
      document.body.setAttribute('data-theme', 'dark');
    }
  }

  private onSessionExpiration(event: AppEvent) {
    this.warningToast('Your session has expired');
    this.logout();
  }

  private onNewUserSession(event: AppEvent) {
    const newUserSession: UserSession = <UserSession>event.eventData;
    this.getCoreModel().userSession$.set(newUserSession);
    this.getCoreModel().userLoggedIn$.set(true);

    this.translateService.use(newUserSession.languageCode || 'en');

    this.socketIoService.connect();

    // Populate cache for cloned line mappings.
    this.clonedLineMappingRepository.clearCache();
    this.clonedLineMappingRepository.getClonedLineMappings().subscribe(() => {});

    this.getCoreModel().impersonation$.set(this.userSessionRepository.isImpersonation());

    if(!this.userSessionRepository.isImpersonation()) {
      const userSession = this.userSessionRepository.getUserSession();

      if(userSession) {
        this.userRepository.getUser(userSession.userId).subscribe({
          next: (user) => {
            this.pendoService.initializePendo(userSession, user);
          },
          error: (error) => {
            console.log(`Failed to fetch user object to initialize pendo`);
          }
        });
      }
    }
  }

  private onNavigationData(event: AppEvent) {
    const url = event.eventData['url'];
    const routeData = event.eventData['routeData'];
    const navigationDisabled = routeData['navigationDisabled'] || false;
    const translationDisabled = routeData['translationDisabled'] || false;
    const alertNotificationDisabled = routeData['alertNotificationDisabled'] || false;

    this.getCoreModel().appNavigationFlags$().enableNavigation$.set(!navigationDisabled);
    this.getCoreModel().translationEnabled$.set(!translationDisabled);
    this.getCoreModel().alertNotificationEnabled$.set(!alertNotificationDisabled);

    this.gaService.pageView(url);
  }

  private onLoggedIn(event: AppEvent) {
    const redirectUrl = event.eventData['redirectUrl'];

    this.accessControlService.initializeScreenAccess(AccessControlService.MAIN_APP_COMPONENT).subscribe({
      next: () => {
        if(redirectUrl) {
          this.navigate(redirectUrl);
        }
        else {
          this.navigate('/');
        }
      }
    });
  }

  private onTranslationLoaded(event: any) {
    console.log(`Translation loaded`);
    this.getCoreModel().translationLoaded$.set(true);
    this.appEventBus.dispatch(new AppEvent(AppEventName.TranslationDownloaded, {}));
  }

  private onAddAppStatus(event: AppEvent) {
    const appStatusEvent: AppStatusEvent = event.eventData as AppStatusEvent;
    console.log(`Adding app status: ${JSON.stringify(appStatusEvent)}`);

    this.toastMessageService.showAppErrorStatusMessage(appStatusEvent.getMessage());
  }

  private onRemoveAppStatus(event: AppEvent) {
    const appStatusEvent: AppStatusEvent = event.eventData as AppStatusEvent;
    console.log(`Removing app status: ${JSON.stringify(appStatusEvent)}`);

    this.toastMessageService.clearAppErrorStatusMessage();
  }

  private onAddProgressTrackableItem(event: AppEvent): void {
    const item: ProgressTrackableItem = event.eventData as ProgressTrackableItem;
    this.handleProgressStatus(item, true);
  }

  private onRemoveProgressTrackableItem(event: AppEvent): void {
    const item: ProgressTrackableItem = event.eventData as ProgressTrackableItem;
    this.handleProgressStatus(item, false);
  }

  private handleProgressStatus(item: ProgressTrackableItem, add: boolean) {
    const model = this.getCoreModel();

    if(add) {
      model.progressTrackableItems$.set(model.progressTrackableItems$().concat(item));
    }
    else {
      const newItems = model.progressTrackableItems$().filter((i) => i.key !== item.key);
      model.progressTrackableItems$.set(newItems);
    }

    model.showProgressBar$.set(true);

    if(add && model.progressValue$() === 0) {
      model.progressValue$.set(10);
    }
    else if(!add) {
      const currentValue = model.progressValue$();
      const currentRemaining = 100 - currentValue;
      let newValue = currentValue + Math.floor(currentRemaining / (model.progressTrackableItems$().length + 1));

      if(newValue > 98) {
        newValue = 98;
      }

      model.progressValue$.set(newValue);
    }

    if(model.progressTrackableItems$().length === 0) {
      setTimeout(() => {
        if(model.progressTrackableItems$().length === 0) {
          model.showProgressBar$.set(false);
          model.progressValue$.set(0);
        }
      }, 500);
    }
  }

  private onAlertNotification(event: AppEvent): void {
    if(event.eventData['alert']) {
      const alertInstance: AlertInstance = event.eventData['alert'];

      if(alertInstance.state === 'OPEN' || alertInstance.state === 'ACKNOWLEDGE') {
        this.toastAlertService.showAlertInstance(alertInstance);
      }
    }
  }

  private onLogoutRequest(event: AppEvent): void {
    this.logout();
  }

  private logout(): void {
    this.socketIoService.disconnect();
    this.authenticationService.logout();

    this.getCoreModel().userLoggedIn$.set(false);
    this.toastMessageService.clearAppErrorStatusMessage();

    this.navigate('/login');
  }

  private onLeaveImpersonation(event: AppEvent): void {
    this.userSessionRepository.removeImpersonation();
    this.navigate('/config').then(() => this.getWindow().location.reload());
  }

  protected override enterMobileView(): void {
    const appNavigationFlags = this.getCoreModel().appNavigationFlags$();

    appNavigationFlags.mobileNavigation$.set(true);
    appNavigationFlags.showNavigation$.set(false);

    document.body.setAttribute('mobile-view', 'true');
  }

  protected override exitMobileView(): void {
    const appNavigationFlags = this.getCoreModel().appNavigationFlags$();

    appNavigationFlags.mobileNavigation$.set(false);
    appNavigationFlags.showNavigation$.set(true);

    document.body.setAttribute('mobile-view', 'false');
  }

  protected override processScreenWidth(width: number): void {
    const appNavigationFlags = this.getCoreModel().appNavigationFlags$();

    if(width < CommonConstants.pinnedNavigationThreshold) {
      appNavigationFlags.disablePinning$.set(true);
    }
    else {
      appNavigationFlags.disablePinning$.set(false);
    }
  }

  protected handleMobileNavigationActivation(eventData: any): void {
    const model = this.getCoreModel();
    model.appNavigationFlags$().showNavigation$.set(true);
    model.appNavigationFlags$().pinned$.set(true);
  }

  protected handleMobileNavigationDeactivation(eventData: any): void {
    const model = this.getCoreModel();
    model.appNavigationFlags$().showNavigation$.set(false);
    model.appNavigationFlags$().pinned$.set(false);
  }

  protected handleNavigationPinToggle(eventData: any): void {
    const model = this.getCoreModel();
    const currentPinValue = model.appNavigationFlags$().pinned$();
    const newPinValue = !currentPinValue;

    model.appNavigationFlags$().pinned$.set(newPinValue);
    this.localStorageRepository.saveNavigationPinPreference(newPinValue);

    setTimeout(() => {
      this.getWindow().dispatchEvent(new Event('resize'));
    }, 100);
  }

  protected handleNavigationMouseEnter(eventData: any): void {
    const model = this.getCoreModel();
    model.appNavigationFlags$().hovered$.set(true);
  }

  protected handleNavigationMouseLeave(eventData: any): void {
    const model = this.getCoreModel();
    model.appNavigationFlags$().hovered$.set(false);
  }

  protected onBuildInfoCheck(): void {
    const localBuildNumber = GlobalConfig.buildNumber;

    if(this.isLoggedIn() && !this.outdatedBuild) {
      this.buildInfoRepository.getUiBuildInfo().subscribe({
        next: (buildInfo) => {
          const currentBuildNumber = buildInfo.buildNumber;

          if(currentBuildNumber) {
            if(currentBuildNumber !== localBuildNumber) {
              this.processOutdatedBuild(localBuildNumber, currentBuildNumber);
            }
          }
        },
        error: (error) => {
          console.error(`Failed to check UI build info`);
        }
      });
    }
  }

  protected processOutdatedBuild(localBuildNumber: string, currentBuildNumber: string): void {
    this.outdatedBuild = true;
    this.toastMessageService.showAppAutoUpdateInfoStatusMessage(this.autoUpdateMessage);

    this.processAutoUpdateCountdown(this.autoUpdateCountDown);
  }

  protected processAutoUpdateCountdown(countDown: number): void {
    const model = this.getCoreModel();

    if(this.snoozeRequest) {
      countDown = this.autoUpdateCountDownAfterSnoozed;
      this.snoozeRequest = false;
    }

    if(this.hideAutoUpdateToastAfterSnoozed && countDown < 60) {
      this.toastMessageService.showAppAutoUpdateInfoStatusMessage(this.autoUpdateMessage);
      this.hideAutoUpdateToastAfterSnoozed = false;
    }

    let countDownDisplay = '';
    const countDownMinute = Math.floor(countDown / 60);
    const countDownSecond = Math.floor(countDown % 60);

    if(countDownMinute > 0) {
      countDownDisplay += countDownMinute;
      countDownDisplay += ' minutes';
    }

    if(countDownDisplay.length > 0) {
      countDownDisplay += ' and ';
    }

    countDownDisplay += countDownSecond;
    countDownDisplay += ' seconds';

    model.autoUpdateCountDownMessage$.set(`Auto update in ${countDownDisplay}`);

    setTimeout(() => {
      const timestamp = Date.now();

      if(this.previousCountDownTimestamp === -1) {
        this.previousCountDownTimestamp = timestamp;
      }

      let delta = timestamp - this.previousCountDownTimestamp;

      this.previousCountDownTimestamp = timestamp;

      if(delta > 0) {
        delta = Math.floor(delta / 1000);
      }

      if(delta < 1) {
        delta = 1;
      }
      else if(delta > countDown) {
        delta = countDown;
      }

      if(countDown > 0) {
        this.processAutoUpdateCountdown(countDown - delta);
      }
      else {
        this.getWindow().location.reload();
      }
    }, 1000);
  }

  protected handleUpdateAppVersion(eventData: any): void {
    this.getWindow().location.reload();
  }

  protected handleSnoozeUpdateAppVersion(eventData: any): void {
    this.snoozeRequest = true;
    this.hideAutoUpdateToastAfterSnoozed = true;
    this.previousCountDownTimestamp = -1;

    this.toastMessageService.clearAppAutoUpdateInfoStatusMessage();
  }

  protected setupGlobalEventListeners(): void {
    this.getWindow().addEventListener('click', (event) => {
      this.gaService.trackClick(event);
    }, true);

    this.getWindow().addEventListener('touchend', (event) => {
      this.gaService.trackTouchEnd(event);
    }, true);

    // this.getWindow().addEventListener('keyup', (event) => {
    //   this.gaService.trackKeyUp(event);
    // });
  }
}
